import { createApi } from '@reduxjs/toolkit/query/react'
import { Patient } from '../types'
import { axiosBaseQuery } from './baseQuery'
import { generatePaginationQuery } from '../utils/service/service-helper'
import {
  BatchCreatePatientsResponseItem,
  CreatePatientRequest,
  emptyPatient,
  transformPatientRequestPayload,
} from '../utils/patient/patientPayload'

interface PatientsQueryObj {
  prescriberId?: string
  page?: number
  limit?: number
  searchTerm?: string
}

interface CreatePatientQueryObj {
  prescriberId?: string
  patient?: CreatePatientRequest
}

interface BatchCreatePatientsQueryObj {
  prescriberId?: string
  patients?: CreatePatientRequest[]
}

interface UpdatePatientQueryObj {
  prescriberId?: string
  patient: Patient
  patientId?: string
}

// Define a service using a base URL and expected endpoints
export const patientsApi = createApi({
  reducerPath: 'patientsApi',
  tagTypes: ['Patients'],
  baseQuery: axiosBaseQuery({
    baseUrl: '',
  }),
  endpoints: (build) => ({
    getPatients: build.query<
      { patients: Patient[]; meta: { totalPages: number; totalItems: number } },
      PatientsQueryObj
    >({
      query: ({ page, limit, searchTerm, prescriberId }) => {
        if (
          process.env.REACT_APP_FEATURE_TOGGLE_SHARED_PATIENTS_ENABLED !== 'true' &&
          !prescriberId
        ) {
          throw new Error('Prescriber id is required to get patients.')
        }

        const queryStr = generatePaginationQuery(limit, page, searchTerm, prescriberId)
        return {
          url: `/bff/patients?${queryStr}`,
          method: 'get',
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.patients.map(({ id }) => ({ type: 'Patients' as const, id })),
              { type: 'Patients', id: 'PARTIAL-LIST' },
            ]
          : [{ type: 'Patients', id: 'PARTIAL-LIST' }],
    }),
    createPatient: build.mutation<Patient, CreatePatientQueryObj>({
      query: ({ patient, prescriberId }) => {
        if (!patient) {
          throw new Error('New patient is required to create patient.')
        }

        if (
          !prescriberId &&
          process.env.REACT_APP_FEATURE_TOGGLE_SHARED_PATIENTS_ENABLED !== 'true'
        ) {
          throw new Error('Prescriber id is required to create patient.')
        }

        return {
          url: `/bff/patients`,
          method: 'post',
          data: {
            patient,
            prescriberId,
          },
        }
      },
      invalidatesTags: ['Patients'],
    }),
    batchCreatePatients: build.mutation<
      BatchCreatePatientsResponseItem[],
      BatchCreatePatientsQueryObj
    >({
      query: ({ patients, prescriberId }) => {
        if (
          process.env.REACT_APP_FEATURE_TOGGLE_SHARED_PATIENTS_ENABLED !== 'true' &&
          !prescriberId
        ) {
          throw new Error('Prescriber id is required to batch create patients.')
        }

        if (!patients?.length) {
          throw new Error('Patients data are required to batch create patients.')
        }
        const formattedPatients = patients.map((patient) => {
          const fullPatientData = { ...emptyPatient, ...patient }
          return transformPatientRequestPayload<CreatePatientRequest>(fullPatientData)
        })
        return {
          url: `/bff/patients/batch`,
          method: 'post',
          data: {
            patients: formattedPatients,
            prescriberId: prescriberId,
          },
        }
      },
      invalidatesTags: ['Patients'],
    }),
    deletePatient: build.mutation<null, { patientId?: string; prescriberId?: string }>({
      query: ({ patientId, prescriberId }) => {
        if (!patientId) {
          throw new Error('Patient id is required to delete patient.')
        }

        const isSharedPatientsEnabled =
          process.env.REACT_APP_FEATURE_TOGGLE_SHARED_PATIENTS_ENABLED === 'true'

        if (!isSharedPatientsEnabled && !prescriberId) {
          throw new Error('Prescriber id is required to delete patient.')
        }

        const url = isSharedPatientsEnabled
          ? `/bff/patients/${patientId}`
          : `/bff/patients/${patientId}/prescriber/${prescriberId}`

        return {
          url,
          method: 'delete',
        }
      },
      invalidatesTags: (result, error, { patientId }) => [
        { type: 'Patients', id: patientId },
        { type: 'Patients', id: 'PARTIAL-LIST' },
      ],
    }),
    updatePatient: build.mutation<Patient, UpdatePatientQueryObj>({
      query: ({ patient, patientId, prescriberId }) => {
        if (!patientId) {
          throw new Error('Patient id is required to update patient.')
        }

        if (
          (!prescriberId &&
            process.env.REACT_APP_FEATURE_TOGGLE_SHARED_PATIENTS_ENABLED !== 'true') ||
          !patientId
        ) {
          throw new Error('Prescriber id and patient id are required to update patient.')
        }

        return {
          url: `/bff/patients/${patientId}`,
          method: 'patch',
          data: {
            patient,
            prescriberId,
          },
        }
      },
      invalidatesTags: (result, error, { patientId }) => [{ type: 'Patients', id: patientId }],
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useUpdatePatientMutation,
  useDeletePatientMutation,
  useCreatePatientMutation,
  useBatchCreatePatientsMutation,
  useLazyGetPatientsQuery,
  useGetPatientsQuery,
} = patientsApi
